/**
 * The only place the app talks HTTP. Same-origin cookies carry the session;
 * every unsafe request also sends the CSRF token the server issued.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(status: number, code: string, message: string, details?: unknown, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** Field-level messages from a 422 response, keyed by field name. */
  fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    const details = this.details as { fields?: { field: string | null; message: string }[]; field?: string } | undefined;
    for (const item of details?.fields ?? []) {
      if (item.field) result[item.field] = item.message;
    }
    if (details?.field) result[details.field] = this.message;
    return result;
  }
}

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);
let csrfToken: string | null = readCookie("pl_csrf");
const sessionListeners = new Set<() => void>();

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((part) => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Called when the server says the session is gone (expired or signed out elsewhere). */
export function onSessionExpired(listener: () => void): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

type Options = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | null | undefined>;
};

function buildUrl(path: string, query?: Options["query"]): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function send(path: string, options: Options): Promise<Response> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (UNSAFE.has(method)) {
    csrfToken = csrfToken ?? readCookie("pl_csrf");
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  }
  return fetch(buildUrl(path, options.query), {
    method,
    headers,
    credentials: "same-origin",
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });
}

async function toError(response: Response): Promise<ApiError> {
  let payload: { error?: { code?: string; message?: string; details?: unknown; request_id?: string } } = {};
  try {
    payload = await response.json();
  } catch {
    /* non-JSON error (proxy, gateway) */
  }
  // Every Person Learn error has an `error` envelope; anything else came from
  // something in between (a proxy or another service), so say which status it was.
  const error = payload.error ?? {};
  const fallback =
    response.status >= 500
      ? "Something went wrong on our side. Your saved work is safe — please try again."
      : `That didn't work (HTTP ${response.status}). Please try again.`;
  return new ApiError(response.status, error.code ?? `http_${response.status}`, error.message ?? fallback,
    error.details, error.request_id);
}

export async function api<T>(path: string, options: Options = {}): Promise<T> {
  let response: Response;
  try {
    response = await send(path, options);
    if (response.status === 401 && UNSAFE.has((options.method ?? "GET").toUpperCase())) {
      const err = await toError(response.clone());
      if (err.code === "csrf_failed") {
        // The token rotated (another tab, a reload): fetch a fresh one and retry once.
        const session = await fetch("/api/v1/auth/session", { credentials: "same-origin" });
        if (session.ok) {
          setCsrfToken(((await session.json()) as { csrf_token: string }).csrf_token);
          response = await send(path, options);
        }
      }
    }
  } catch (cause) {
    if ((cause as Error).name === "AbortError") throw cause;
    throw new ApiError(0, "network_error", "Can't reach Person Learn right now. Check your connection and try again.");
  }
  if (!response.ok) {
    const err = await toError(response);
    if (response.status === 401 && ["not_authenticated", "session_expired"].includes(err.code)) {
      sessionListeners.forEach((listener) => listener());
    }
    throw err;
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const get = <T>(path: string, query?: Options["query"], signal?: AbortSignal) =>
  api<T>(path, { query, signal });
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: "POST", body: body ?? undefined });
export const put = <T>(path: string, body?: unknown) => api<T>(path, { method: "PUT", body });
export const patch = <T>(path: string, body?: unknown) => api<T>(path, { method: "PATCH", body });
export const del = <T>(path: string) => api<T>(path, { method: "DELETE" });

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something unexpected happened. Please try again.";
}
